import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Column } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import { useSupabaseClient } from 'src/api/supabase'
import { queryKeys } from 'src/api/db/queryKeys'
import { CategoryCard } from './components/CategoryCard'
import { ExpandedCategoryCard } from './components/ExpandedCategoryCard'
import type { Category } from './types'

export const CategoryViewer = ({
  expandedCategoryId,
  onExpandedCategoryChange,
  onClose,
}: {
  expandedCategoryId?: string | null
  onExpandedCategoryChange?: (id: string | null) => void
  onClose?: () => void
}) => {
  const supabase = useSupabaseClient()
  const [internalExpandedId, setInternalExpandedId] = useState<string | null>(
    null
  )
  const isControlled = expandedCategoryId !== undefined
  const effectiveExpandedId = isControlled
    ? expandedCategoryId ?? null
    : internalExpandedId
  const setExpandedId = isControlled
    ? (id: string | null) => onExpandedCategoryChange?.(id)
    : setInternalExpandedId

  const {
    data: dbCategories,
    isLoading,
    error,
  } = useQuery({
    queryKey: [queryKeys.categories, 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('category')
        .select('id, title, parent_category_id, color')
        .order('title', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })

  const categories = useMemo((): Category[] => {
    if (!dbCategories) return []
    const roots = dbCategories?.filter((c) => c.parent_category_id === null)
    const byParent = new Map<number, typeof dbCategories>()
    for (const c of dbCategories) {
      if (c.parent_category_id !== null) {
        const arr = byParent.get(c.parent_category_id) ?? []
        arr.push(c)
        byParent.set(c.parent_category_id, arr)
      }
    }
    return roots.map((cat) => {
      const children = (byParent.get(cat.id) ?? [])
        .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))
        .map((sub) => ({
          id: String(sub.id),
          label: sub.title ?? '',
        }))
      return {
        id: String(cat.id),
        label: cat.title ?? '',
        color: cat.color ?? null,
        subCategories: children,
      }
    })
  }, [dbCategories])

  const expandedIndex = effectiveExpandedId
    ? categories.findIndex((c) => c.id === effectiveExpandedId)
    : -1
  const expandedCategory =
    expandedIndex >= 0 ? categories[expandedIndex] : null
  if (isLoading) {
    return (
      <Column className="relative w-full min-w-0">
        <div className="grid grid-cols-2 gap-3 min-w-0 sm:grid-cols-3 sm:gap-4">
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className="aspect-[3/2] rounded-[var(--radius-card)] border border-[hsl(var(--border-subtle))] bg-muted/50 animate-pulse"
            />
          ))}
        </div>
      </Column>
    )
  }

  if (error) {
    return (
      <Column className="relative w-full min-w-0 p-4">
        <p className="text-sm text-muted-foreground">
          Could not load categories. Please try again later.
        </p>
      </Column>
    )
  }

  if (categories.length === 0) {
    return (
      <Column className="relative w-full min-w-0 p-4">
        <p className="text-sm text-muted-foreground">No categories available.</p>
      </Column>
    )
  }

  return (
    <Column className="relative w-full gap-4">
      <div
        className={cn(
          'grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4',
          effectiveExpandedId && 'invisible pointer-events-none select-none'
        )}
        aria-hidden={effectiveExpandedId ? true : undefined}
      >
        {categories.map((category) => (
          <CategoryCard
            key={category.id}
            category={category}
            onClick={() => setExpandedId(category.id)}
          />
        ))}
      </div>

      {expandedCategory && (
        <ExpandedCategoryCard
          category={expandedCategory}
          onMinimize={() => setExpandedId(null)}
          onClose={onClose}
        />
      )}
    </Column>
  )
}
